import type { ClosedReason } from '@akpl/shared';
import { Brand, Button } from '@akpl/ui-tv';

import { useRouter } from '../router';
import { useStore } from '../store';
import { useT } from '../useT';
import styles from './screens.module.css';
import type { ScreenProps } from './types';
import { useScreenFocus } from './useScreenFocus';

export const SESSION_ENDED_FOCUS = 'session-ended-repair';

/**
 * QA 2026-09-15 UX-566: the body says what actually happened. `meta.closedReason` reaches the
 * store through `endLiveSession`; a session closed without one (an older deploy, or a
 * `session_expired` answer that arrived before the listener) keeps the generic sentence.
 */
const BODY_KEY = {
  expired: 'tv:sessionEnded.reason.expired',
  host_logout: 'tv:sessionEnded.reason.host_logout',
  admin: 'tv:sessionEnded.reason.admin',
  tv_unpaired: 'tv:sessionEnded.reason.tv_unpaired',
} as const satisfies Record<ClosedReason, string>;

/**
 * TV-11 "La sesión terminó" (docs/04 §5): reached from `meta.status='closed'` or a
 * `session_expired` API answer. The single action starts pairing over.
 */
export default function SessionEnded(_props: ScreenProps<'sessionEnded'>) {
  const { t } = useT();
  const reset = useRouter((s) => s.reset);
  const supportEmail = useStore((s) => s.config?.supportEmail ?? '');
  const endReason = useStore((s) => s.endReason);
  useScreenFocus('sessionEnded', SESSION_ENDED_FOCUS);

  return (
    <div className={styles.fullscreen} data-testid="screen-session-ended">
      <div className={styles.ended}>
        <Brand name={t('common:app.name')} />
        <h1 className={styles.endedTitle}>{t('tv:sessionEnded.title')}</h1>
        <p className={styles.endedBody}>
          {t(endReason ? BODY_KEY[endReason] : 'tv:sessionEnded.body')}
        </p>
        <Button
          focusKey={SESSION_ENDED_FOCUS}
          variant="primary"
          onSelect={() => reset('pairing')}
          data-testid="session-ended-repair"
        >
          {t('tv:sessionEnded.backToPairing')}
        </Button>
        {supportEmail ? (
          <p className={styles.endedHelp}>{t('tv:pairing.help', { email: supportEmail })}</p>
        ) : null}
      </div>
    </div>
  );
}
